import { useEffect, useState } from "react";
import { Gift, Star, Plus, Pencil, Trash2, Send, Crown, Coins, Ticket, History, Settings2, Award } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";

const emptyReward = { name: "", cost_points: "", type: "product_free", value: "" };

export default function Club() {
  const [rewards, setRewards] = useState([]);
  const [clients, setClients] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [rewardOpen, setRewardOpen] = useState(false);
  const [rewardForm, setRewardForm] = useState(emptyReward);
  const [rewardEditId, setRewardEditId] = useState(null);
  const [redeemClient, setRedeemClient] = useState(null);
  const [historyClient, setHistoryClient] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [busy, setBusy] = useState(null);
  const [settings, setSettings] = useState({ pass_bonus_points: 200, vip_threshold: 2000 });
  const [savingSettings, setSavingSettings] = useState(false);
  const [monthlyBusy, setMonthlyBusy] = useState(false);
  const [pinCode, setPinCode] = useState("");
  const [pinAlreadySet, setPinAlreadySet] = useState(false);
  const [monthlyPreview, setMonthlyPreview] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  const downloadBackup = async () => {
    try {
      const res = await api.get("/accounting/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.download = `sauvegarde_avant_reset_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Sauvegarde Excel téléchargée");
      return true;
    } catch {
      return false;
    }
  };

  const resetData = async () => {
    setResetBusy(true);
    try {
      const backed = await downloadBackup();
      if (!backed) {
        toast.error("Échec de la sauvegarde — réinitialisation annulée");
        return;
      }
      const r = await api.post("/admin/reset-data");
      const d = r.data.deleted;
      toast.success(`Données réinitialisées (${d.clients} clients, ${d.orders} commandes supprimés)`);
      setResetOpen(false);
      load();
    } catch {
      toast.error("Échec de la réinitialisation");
    } finally {
      setResetBusy(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        pass_bonus_points: parseInt(settings.pass_bonus_points || 0, 10),
        vip_threshold: parseInt(settings.vip_threshold || 0, 10),
        pin_enabled: !!settings.pin_enabled,
        pin_code: pinCode ? pinCode.trim() : null,
      };
      if (payload.pin_enabled && !pinAlreadySet && !payload.pin_code) {
        toast.error("Définissez un code PIN pour activer la protection");
        setSavingSettings(false);
        return;
      }
      const r = await api.put("/settings", payload);
      setSettings(r.data);
      setPinCode("");
      toast.success("Paramètres du Club enregistrés");
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingSettings(false);
    }
  };

  const runMonthly = async () => {
    try {
      const r = await api.get("/donna/monthly-preview");
      setMonthlyPreview(r.data);
    } catch {
      toast.error("Impossible de charger la liste des destinataires");
    }
  };

  const confirmMonthly = async () => {
    setMonthlyBusy(true);
    try {
      const r = await api.post("/donna/run-monthly-balance");
      setMonthlyPreview(null);
      if (r.data.configured === false) toast.warning("WhatsApp non configuré");
      else toast.success(`Soldes envoyés à ${r.data.sent} membre(s)`);
    } catch {
      toast.error("Échec de l'envoi mensuel");
    } finally {
      setMonthlyBusy(false);
    }
  };

  const openHistory = async (c) => {
    setHistoryClient(c);
    try {
      const r = await api.get(`/clients/${c.id}/points`);
      setHistoryEntries(r.data);
    } catch {
      setHistoryEntries([]);
    }
  };

  const load = () => {
    api.get("/rewards").then((r) => setRewards(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/redemptions").then((r) => setRedemptions(r.data)).catch(() => {});
    api.get("/settings").then((r) => { setSettings(r.data); setPinAlreadySet(!!r.data.pin_enabled); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const pointsInCirculation = clients.reduce((s, c) => s + (c.points_balance || 0), 0);
  const sortedClients = [...clients].sort((a, b) => (b.points_balance || 0) - (a.points_balance || 0));

  const openNewReward = () => { setRewardForm(emptyReward); setRewardEditId(null); setRewardOpen(true); };
  const openEditReward = (r) => {
    setRewardForm({ name: r.name, cost_points: r.cost_points, type: r.type, value: r.value || "" });
    setRewardEditId(r.id);
    setRewardOpen(true);
  };

  const saveReward = async () => {
    if (!rewardForm.name || rewardForm.cost_points === "") { toast.error("Nom et coût en points requis"); return; }
    const payload = {
      name: rewardForm.name,
      cost_points: parseInt(rewardForm.cost_points, 10),
      type: rewardForm.type,
      value: rewardForm.type === "discount" ? parseInt(rewardForm.value || 0, 10) : 0,
    };
    try {
      if (rewardEditId) await api.put(`/rewards/${rewardEditId}`, payload);
      else await api.post("/rewards", payload);
      toast.success(rewardEditId ? "Récompense modifiée" : "Récompense ajoutée");
      setRewardOpen(false); load();
    } catch { toast.error("Erreur"); }
  };

  const removeReward = async (id) => { await api.delete(`/rewards/${id}`); toast.success("Récompense supprimée"); load(); };

  const redeem = async (client, reward) => {
    setBusy(reward.id);
    try {
      await api.post(`/clients/${client.id}/redeem/${reward.id}`);
      toast.success(`« ${reward.name} » échangé pour ${client.name}`, { icon: "🎁" });
      const fresh = await api.get("/clients");
      setClients(fresh.data);
      setRedeemClient(fresh.data.find((c) => c.id === client.id) || null);
      api.get("/redemptions").then((r) => setRedemptions(r.data)).catch(() => {});
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec de l'échange");
    } finally { setBusy(null); }
  };

  const sendBalance = async (client) => {
    setBusy(`wa-${client.id}`);
    try {
      const res = await api.post(`/donna/send-balance/${client.id}`);
      if (res.data.sent) toast.success(`Solde envoyé à ${client.name}`);
      else if (res.data.configured === false) toast.warning("WhatsApp non configuré", { description: res.data.message });
      else toast.error(res.data.error || "Échec de l'envoi WhatsApp");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec");
    } finally { setBusy(null); }
  };

  const rewardBadge = (r) => (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.type === "discount" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>
      {r.type === "discount" ? "Remise" : "Produit offert"}
    </span>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in-up" data-testid="club-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Fidélité</p>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">Club Privilège Diabliss</h1>
        <p className="text-slate-500 mt-1 text-sm">NaturaPoints : 1 point pour chaque 100 FCFA dépensés.</p>
      </div>

      {/* Paramètres du Club */}
      <div className="bg-white rounded-lg border border-slate-200 p-6" data-testid="club-settings">
        <div className="flex items-center gap-2 mb-4">
          <Settings2 className="h-5 w-5 text-slate-500" />
          <h2 className="font-display text-xl font-semibold text-slate-800">Paramètres du Club</h2>
        </div>
        <div className="flex flex-wrap items-end gap-6">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Bonus Pass Or (points)</label>
            <input data-testid="setting-bonus-input" type="number" value={settings.pass_bonus_points} onChange={(e) => setSettings({ ...settings, pass_bonus_points: e.target.value })} className="w-40 px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Seuil statut VIP (points cumulés)</label>
            <input data-testid="setting-vip-input" type="number" value={settings.vip_threshold} onChange={(e) => setSettings({ ...settings, vip_threshold: e.target.value })} className="w-48 px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              <input data-testid="setting-pin-toggle" type="checkbox" checked={!!settings.pin_enabled} onChange={(e) => setSettings({ ...settings, pin_enabled: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-sky-600" />
              Protéger par code PIN
            </label>
            <input data-testid="setting-pin-input" type="password" inputMode="numeric" value={pinCode} onChange={(e) => setPinCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder={pinAlreadySet ? "•••• (défini)" : "4 à 6 chiffres"} disabled={!settings.pin_enabled} className="w-40 px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-slate-50 disabled:text-slate-400" />
          </div>
          <button data-testid="save-settings-button" onClick={saveSettings} disabled={savingSettings} className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-colors duration-200 disabled:opacity-60">
            {savingSettings ? "Enregistrement…" : "Enregistrer"}
          </button>
          <button data-testid="run-monthly-button" onClick={runMonthly} disabled={monthlyBusy} className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors duration-200 disabled:opacity-60">
            <Send className="h-4 w-4" /> {monthlyBusy ? "Envoi…" : "Envoyer les soldes du mois"}
          </button>
        </div>
      </div>

      {/* Zone dangereuse — réinitialisation des données */}
      <div className="bg-white rounded-lg border border-red-200 p-6" data-testid="danger-zone">
        <div className="flex items-center gap-2 mb-2">
          <Trash2 className="h-5 w-5 text-red-500" />
          <h2 className="font-display text-xl font-semibold text-red-700">Zone dangereuse</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4 max-w-2xl">
          Supprime définitivement <strong>tous les clients, commandes, historique de points et cadeaux échangés</strong> et remet les compteurs à zéro.
          Les récompenses, produits, paramètres et votre compte administrateur sont conservés. Cette action est irréversible.
        </p>
        <button data-testid="open-reset-button" onClick={() => setResetOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors duration-200">
          <Trash2 className="h-4 w-4" /> Réinitialiser les données
        </button>
      </div>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent data-testid="reset-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Réinitialiser toutes les données ?</AlertDialogTitle>
            <AlertDialogDescription>
              Une <strong>sauvegarde Excel</strong> sera téléchargée automatiquement, puis tous les clients, commandes, historiques de points et cadeaux échangés seront <strong>définitivement supprimés</strong> et les compteurs remis à zéro.
              Les récompenses, produits et paramètres seront conservés. Cette action ne peut pas être annulée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <button data-testid="reset-cancel-button" onClick={() => setResetOpen(false)} disabled={resetBusy} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors duration-200 disabled:opacity-60">
              Annuler
            </button>
            <button data-testid="reset-confirm-button" onClick={resetData} disabled={resetBusy} className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors duration-200 disabled:opacity-60">
              {resetBusy ? "Suppression…" : "Oui, tout supprimer"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border border-slate-200 p-6" data-testid="club-kpi-points">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Points en circulation</span>
            <div className="h-9 w-9 rounded-lg bg-sky-50 flex items-center justify-center"><Coins className="h-[18px] w-[18px] text-sky-600" /></div>
          </div>
          <p className="font-display text-3xl font-bold text-slate-900 tabular">{pointsInCirculation.toLocaleString("fr-FR").replace(/\u202f|,/g, " ")} pts</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-6" data-testid="club-kpi-rewards">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Cadeaux au catalogue</span>
            <div className="h-9 w-9 rounded-lg bg-amber-50 flex items-center justify-center"><Gift className="h-[18px] w-[18px] text-amber-600" /></div>
          </div>
          <p className="font-display text-3xl font-bold text-slate-900 tabular">{rewards.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-6" data-testid="club-kpi-redemptions">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Cadeaux échangés</span>
            <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center"><Ticket className="h-[18px] w-[18px] text-emerald-600" /></div>
          </div>
          <p className="font-display text-3xl font-bold text-slate-900 tabular">{redemptions.length}</p>
        </div>
      </div>

      {/* Boutique Cadeaux */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-amber-500" />
            <h2 className="font-display text-2xl font-semibold text-slate-800">Boutique Cadeaux</h2>
          </div>
          <button data-testid="add-reward-button" onClick={openNewReward} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0ea5e9] hover:bg-[#0284c7] text-white font-semibold text-sm transition-colors duration-200">
            <Plus className="h-4 w-4" /> Nouvelle récompense
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rewards.map((r) => (
            <div key={r.id} data-testid={`reward-card-${r.id}`} className="bg-white rounded-lg border border-slate-200 p-5 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-200">
              <div className="flex items-start justify-between mb-3">
                <div className="h-11 w-11 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Gift className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex gap-1">
                  <button data-testid={`edit-reward-${r.id}`} onClick={() => openEditReward(r)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 transition-colors duration-150"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`delete-reward-${r.id}`} onClick={() => removeReward(r.id)} className="p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors duration-150"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">{r.name}</h3>
              <div className="flex items-center gap-2 mb-3">{rewardBadge(r)}{r.type === "discount" && <span className="text-xs text-slate-500">{formatFCFA(r.value)}</span>}</div>
              <div className="flex items-center gap-1.5 text-amber-700 font-bold">
                <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                <span className="tabular">{r.cost_points} pts</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* NaturaPoints - soldes clients */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Coins className="h-5 w-5 text-sky-500" />
          <h2 className="font-display text-2xl font-semibold text-slate-800">Soldes NaturaPoints</h2>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left">
                <th className="px-6 py-3.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Client</th>
                <th className="px-6 py-3.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Pass Or</th>
                <th className="px-6 py-3.5 font-semibold text-slate-500 text-xs uppercase tracking-wider text-right">Solde</th>
                <th className="px-6 py-3.5"></th>
              </tr>
            </thead>
            <tbody>
              {sortedClients.length === 0 && (
                <tr><td colSpan={4} className="px-6 py-12 text-center text-slate-400">Aucun client.</td></tr>
              )}
              {sortedClients.map((c) => {
                const active = c.pass_actif && !c.pass_expired;
                return (
                  <tr key={c.id} data-testid={`points-row-${c.id}`} className="border-b border-slate-50 hover:bg-slate-50 transition-colors duration-150">
                    <td className="px-6 py-4 font-medium text-slate-800">
                      {c.name}
                      {c.is_vip && (
                        <span data-testid={`vip-badge-${c.id}`} className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 inline-flex items-center gap-1"><Award className="h-3 w-3" /> VIP</span>
                      )}
                      <span className="block text-xs text-slate-400">{c.phone}</span>
                    </td>
                    <td className="px-6 py-4">
                      {active ? (
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 inline-flex items-center gap-1"><Crown className="h-3 w-3" /> Pass Or</span>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="tabular font-bold text-sky-700 inline-flex items-center gap-1 justify-end"><Star className="h-3.5 w-3.5 fill-sky-400 text-sky-400" />{c.points_balance || 0} pts</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button data-testid={`open-redeem-${c.id}`} onClick={() => setRedeemClient(c)} className="px-3 py-1.5 rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 text-xs font-semibold transition-colors duration-150 inline-flex items-center gap-1">
                          <Gift className="h-3.5 w-3.5" /> Échanger
                        </button>
                        <button data-testid={`open-history-${c.id}`} onClick={() => openHistory(c)} className="px-3 py-1.5 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 text-xs font-semibold transition-colors duration-150 inline-flex items-center gap-1">
                          <History className="h-3.5 w-3.5" /> Historique
                        </button>
                        <button data-testid={`send-balance-${c.id}`} onClick={() => sendBalance(c)} disabled={busy === `wa-${c.id}`} className="px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold transition-colors duration-150 inline-flex items-center gap-1 disabled:opacity-60">
                          <Send className="h-3.5 w-3.5" /> Solde WhatsApp
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Reward dialog */}
      <Dialog open={rewardOpen} onOpenChange={setRewardOpen}>
        <DialogContent data-testid="reward-dialog">
          <DialogHeader><DialogTitle className="font-display">{rewardEditId ? "Modifier la récompense" : "Nouvelle récompense"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nom</label>
              <input data-testid="reward-name-input" value={rewardForm.name} onChange={(e) => setRewardForm({ ...rewardForm, name: e.target.value })} placeholder="1 sachet 200g offert" className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Coût (points)</label>
                <input data-testid="reward-cost-input" type="number" value={rewardForm.cost_points} onChange={(e) => setRewardForm({ ...rewardForm, cost_points: e.target.value })} placeholder="700" className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Type</label>
                <Select value={rewardForm.type} onValueChange={(v) => setRewardForm({ ...rewardForm, type: v })}>
                  <SelectTrigger data-testid="reward-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product_free">Produit offert</SelectItem>
                    <SelectItem value="discount">Remise (FCFA)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {rewardForm.type === "discount" && (
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Montant de la remise (FCFA)</label>
                <input data-testid="reward-value-input" type="number" value={rewardForm.value} onChange={(e) => setRewardForm({ ...rewardForm, value: e.target.value })} placeholder="5000" className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setRewardOpen(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium transition-colors duration-200">Annuler</button>
            <button data-testid="save-reward-button" onClick={saveReward} className="px-4 py-2 rounded-lg bg-[#0ea5e9] hover:bg-[#0284c7] text-white text-sm font-semibold transition-colors duration-200">Enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redeem dialog */}
      <Dialog open={!!redeemClient} onOpenChange={(o) => !o && setRedeemClient(null)}>
        <DialogContent data-testid="redeem-dialog">
          <DialogHeader><DialogTitle className="font-display">Échanger des points</DialogTitle></DialogHeader>
          {redeemClient && (
            <div className="py-2">
              <div className="flex items-center justify-between mb-4 p-3 rounded-lg bg-sky-50">
                <span className="font-medium text-slate-800">{redeemClient.name}</span>
                <span className="tabular font-bold text-sky-700 inline-flex items-center gap-1"><Star className="h-4 w-4 fill-sky-400 text-sky-400" />{redeemClient.points_balance || 0} pts</span>
              </div>
              <div className="space-y-2">
                {rewards.map((r) => {
                  const canAfford = (redeemClient.points_balance || 0) >= r.cost_points;
                  return (
                    <div key={r.id} data-testid={`redeem-option-${r.id}`} className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{r.name}</p>
                        <p className="text-xs text-amber-700 font-semibold tabular">{r.cost_points} pts</p>
                      </div>
                      <button
                        data-testid={`redeem-confirm-${r.id}`}
                        onClick={() => redeem(redeemClient, r)}
                        disabled={!canAfford || busy === r.id}
                        className="px-3 py-1.5 rounded-md bg-[#0ea5e9] hover:bg-[#0284c7] text-white text-xs font-semibold transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {canAfford ? "Échanger" : "Insuffisant"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Points history dialog */}
      <Dialog open={!!historyClient} onOpenChange={(o) => !o && setHistoryClient(null)}>        <DialogContent data-testid="history-dialog">
          <DialogHeader><DialogTitle className="font-display">Historique des points{historyClient ? ` — ${historyClient.name}` : ""}</DialogTitle></DialogHeader>
          <div className="py-2 max-h-96 overflow-y-auto">
            {historyEntries.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">Aucun mouvement de points pour l'instant.</p>
            ) : (
              <div className="space-y-2">
                {historyEntries.map((e) => (
                  <div key={e.id} data-testid={`history-entry-${e.id}`} className="flex items-center justify-between p-3 rounded-lg border border-slate-100">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{e.label}</p>
                      <p className="text-xs text-slate-400 tabular">{formatDate(e.date)}</p>
                    </div>
                    <span className={`tabular font-bold ${e.points >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                      {e.points >= 0 ? "+" : ""}{e.points} pts
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Monthly reminder preview dialog */}
      <Dialog open={!!monthlyPreview} onOpenChange={(o) => !o && setMonthlyPreview(null)}>
        <DialogContent data-testid="monthly-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Rappel mensuel — destinataires</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-slate-500 mb-4">
              {monthlyPreview && monthlyPreview.length > 0
                ? `${monthlyPreview.length} membre(s) Pass Or actif(s) recevront leur solde par WhatsApp :`
                : "Aucun membre Pass Or actif à contacter pour le moment."}
            </p>
            <div className="max-h-72 overflow-y-auto space-y-2">
              {(monthlyPreview || []).map((m) => (
                <div key={m.id} data-testid={`preview-row-${m.id}`} className="flex items-center justify-between p-3 rounded-lg border border-slate-100">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{m.name}</p>
                    <p className="text-xs text-slate-400">{m.phone}</p>
                  </div>
                  <span className="tabular text-xs font-bold text-sky-700">{m.points_balance} pts</span>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setMonthlyPreview(null)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium transition-colors duration-200">Annuler</button>
            <button data-testid="confirm-monthly-button" onClick={confirmMonthly} disabled={monthlyBusy || !monthlyPreview || monthlyPreview.length === 0} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors duration-200 disabled:opacity-50">
              <Send className="h-4 w-4" /> {monthlyBusy ? "Envoi…" : "Confirmer l'envoi"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

